import {
	BarChart3,
	Bell,
	Building2,
	CalendarDays,
	Check,
	ChevronDown,
	Compass,
	Home,
	KeyRound,
	Layers,
	type LucideIcon,
	Megaphone,
	MessageSquare,
	Plus,
	Search,
	Settings,
	Shield,
	Sparkles,
	Users,
	Video,
	Webhook,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { CommunityLogo } from "@/src/common/components/app-mock/community-logo";
import {
	MOCK_COMMUNITY,
	MOCK_SIBLING_COMMUNITIES,
	type MockCommunity,
	type MockPerson,
	VIEWER,
} from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, UnreadBadge } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

export type WebNavId =
	| "feed"
	| "members"
	| "messages"
	| "meetings"
	| "events"
	| "groups"
	| "moderation"
	| "admin-analytics"
	| "admin-members"
	| "admin-groups"
	| "admin-ai"
	| "admin-community"
	| "admin-sponsorship"
	| "admin-api-keys"
	| "admin-webhooks";

export const WEB_NAV_PATH: Record<WebNavId, string> = {
	feed: "/feed",
	members: "/members",
	messages: "/messages",
	meetings: "/meetings",
	events: "/events",
	groups: "/groups/discover",
	moderation: "/moderation",
	"admin-analytics": "/admin/analytics",
	"admin-members": "/admin/members",
	"admin-groups": "/admin/groups",
	"admin-ai": "/ai",
	"admin-community": "/admin/community",
	"admin-sponsorship": "/admin/sponsorship",
	"admin-api-keys": "/admin/api-keys",
	"admin-webhooks": "/admin/webhooks",
};

interface NavItem {
	id: WebNavId;
	label: string;
	icon: LucideIcon;
	badge?: number;
}

const PRIMARY_NAV: ReadonlyArray<NavItem> = [
	{ id: "feed", label: "Home", icon: Home },
	{ id: "members", label: "Members", icon: Users },
	{ id: "messages", label: "Messages", icon: MessageSquare, badge: 3 },
	{ id: "meetings", label: "Meetings", icon: Video },
	{ id: "events", label: "Events", icon: CalendarDays },
	{ id: "groups", label: "Discover Groups", icon: Compass },
	{ id: "moderation", label: "Moderation", icon: Shield },
];

const ADMIN_NAV: ReadonlyArray<NavItem> = [
	{ id: "admin-analytics", label: "Analytics", icon: BarChart3 },
	{ id: "admin-members", label: "Members", icon: Users },
	{ id: "admin-groups", label: "Groups", icon: Layers },
	{ id: "admin-ai", label: "AI Insights", icon: Sparkles },
	{ id: "admin-community", label: "Community", icon: Building2 },
	{ id: "admin-sponsorship", label: "Sponsorship", icon: Megaphone },
	{ id: "admin-api-keys", label: "API Keys", icon: KeyRound },
	{ id: "admin-webhooks", label: "Webhooks", icon: Webhook },
];

type WebShellLayout = "desktop" | "compact";

export interface WebAppShellProps {
	activeNav: WebNavId;
	children: ReactNode;
	community?: MockCommunity;
	viewer?: MockPerson;
	rightSidebar?: ReactNode;
	fullBleed?: boolean;
	layout?: WebShellLayout;
	onNavigate?: (id: WebNavId) => void;
	navigable?: ReadonlyArray<WebNavId>;
	panelId?: string;
	navLabel?: string;
	scroll?: boolean;
	className?: string;
}

interface NavRowProps {
	item: NavItem;
	active: boolean;
	sub?: boolean;
	interactive: boolean;
	onNavigate?: (id: WebNavId) => void;
	panelId?: string;
	compact: boolean;
}

function NavRow({ item, active, sub, interactive, onNavigate, panelId, compact }: NavRowProps) {
	const { palette } = useCommunityVariant();
	const Icon = item.icon;
	const iconSize = sub ? 12 : compact ? 14 : 16;
	const baseClass = `flex w-full items-center gap-2.5 rounded-lg px-2.5 ${compact ? "py-1.5 text-[11px]" : "py-2 text-xs"} font-medium transition-colors`;
	const style = active
		? sub
			? { color: palette.accentInk }
			: { background: palette.accentSoftHover, color: palette.accentInk }
		: { color: "rgba(23, 23, 23, 0.7)" };
	const content = (
		<>
			<Icon style={{ width: iconSize, height: iconSize }} className="shrink-0" />
			<span className="truncate">{item.label}</span>
			{item.badge ? <UnreadBadge count={item.badge} tone="accent" className="ml-auto" /> : null}
		</>
	);
	if (interactive && onNavigate) {
		return (
			<button
				type="button"
				role="tab"
				id={`tab-${item.id}`}
				aria-selected={active}
				aria-controls={panelId}
				onClick={() => onNavigate(item.id)}
				className={`${baseClass} hover:bg-black/5`}
				style={style}
				data-mock="nav-row"
			>
				{content}
			</button>
		);
	}
	return (
		<span className={baseClass} style={style} data-mock="nav-row" aria-hidden="true">
			{content}
		</span>
	);
}

interface CommunityRailProps {
	community: MockCommunity;
}

function CommunityRail({ community }: CommunityRailProps) {
	return (
		<aside
			aria-hidden="true"
			className="hidden w-14 shrink-0 flex-col items-center gap-2 border-r bg-white py-3 md:flex"
			style={{ borderColor: MOCK_TOKENS.separator }}
			data-mock="community-rail"
		>
			<span className="relative" title={community.name}>
				<CommunityLogo size={36} rounded="rounded-full" />
				<span className="absolute inset-0 flex items-center justify-center rounded-full bg-neutral-900/20 text-white">
					<Check className="size-3.5" />
				</span>
			</span>
			{MOCK_SIBLING_COMMUNITIES.map((sibling) => (
				<span
					key={sibling.id}
					title={sibling.name}
					className="flex size-9 items-center justify-center rounded-full text-[11px] font-bold text-white"
					style={{ background: sibling.color }}
				>
					{sibling.initials}
				</span>
			))}
			<span
				title="Create a community"
				className="flex size-9 items-center justify-center rounded-full border-2 border-dashed text-neutral-400"
				style={{ borderColor: "rgba(23, 23, 23, 0.3)" }}
			>
				<Plus className="size-4" />
			</span>
		</aside>
	);
}

interface SidebarProps {
	community: MockCommunity;
	activeNav: WebNavId;
	onNavigate?: (id: WebNavId) => void;
	navigable?: ReadonlyArray<WebNavId>;
	panelId?: string;
	navLabel?: string;
	compact: boolean;
}

function Sidebar({ community, activeNav, onNavigate, navigable, panelId, navLabel, compact }: SidebarProps) {
	const { palette } = useCommunityVariant();
	const adminToggleId = useId();
	const isAdminActive = activeNav.startsWith("admin-");
	const [adminState, setAdminState] = useState<{ nav: WebNavId; open: boolean | null }>({ nav: activeNav, open: null });
	if (adminState.nav !== activeNav) {
		setAdminState({ nav: activeNav, open: null });
	}
	const adminOpen = adminState.open ?? isAdminActive;
	const isInteractive = (id: WebNavId) => Boolean(onNavigate) && (navigable ? navigable.includes(id) : true);
	const hasTabs = Boolean(onNavigate);

	return (
		<aside
			className={`${compact ? "hidden w-40 sm:flex" : "hidden w-[200px] md:flex"} shrink-0 flex-col border-r bg-white`}
			style={{ borderColor: MOCK_TOKENS.separator }}
			data-mock="sidebar"
		>
			<div className={compact ? "p-2" : "p-3"}>
				<span className="flex w-full items-center gap-2 rounded-lg p-1" aria-hidden="true">
					<CommunityLogo size={compact ? 22 : 26} rounded="rounded-lg" />
					<span className="truncate text-xs font-semibold text-neutral-900">{community.name}</span>
					<ChevronDown className="ml-auto size-3.5 shrink-0 text-neutral-400" />
				</span>
			</div>
			<div
				className={`mock-scroll min-h-0 flex-1 overflow-y-auto ${compact ? "px-1.5 pb-2" : "px-2 pb-3"}`}
				aria-hidden={hasTabs ? undefined : true}
			>
				<ul className="space-y-0.5" {...(hasTabs ? { role: "tablist", "aria-label": navLabel, "aria-orientation": "vertical" } : {})}>
					{PRIMARY_NAV.map((item) => (
						<li key={item.id} role={hasTabs ? "presentation" : undefined}>
							<NavRow
								item={item}
								active={item.id === activeNav}
								interactive={isInteractive(item.id)}
								onNavigate={onNavigate}
								panelId={panelId}
								compact={compact}
							/>
						</li>
					))}
				</ul>
				<div
					className="mt-3 border-t pt-3"
					style={{ borderColor: MOCK_TOKENS.border }}
					{...(hasTabs ? { role: "group", "aria-labelledby": adminToggleId } : {})}
				>
					{onNavigate ? (
						<button
							type="button"
							id={adminToggleId}
							onClick={() => setAdminState({ nav: activeNav, open: !adminOpen })}
							aria-expanded={adminOpen}
							className={`flex w-full items-center gap-2.5 px-2.5 text-xs font-medium ${compact ? "py-1.5 text-[11px]" : "py-2"} ${
								adminOpen ? "rounded-t-lg" : "rounded-lg"
							}`}
							style={{ background: palette.accentSoft, color: palette.accentInk }}
						>
							<Settings style={{ width: compact ? 14 : 16, height: compact ? 14 : 16 }} />
							<span>Admin</span>
							<ChevronDown className={`ml-auto size-3.5 transition-transform ${adminOpen ? "rotate-180" : ""}`} />
						</button>
					) : (
						<span
							className={`flex w-full items-center gap-2.5 px-2.5 text-xs font-medium ${compact ? "py-1.5 text-[11px]" : "py-2"} ${
								adminOpen ? "rounded-t-lg" : "rounded-lg"
							}`}
							style={{ background: palette.accentSoft, color: palette.accentInk }}
						>
							<Settings style={{ width: compact ? 14 : 16, height: compact ? 14 : 16 }} />
							<span>Admin</span>
							<ChevronDown className={`ml-auto size-3.5 ${adminOpen ? "rotate-180" : ""}`} />
						</span>
					)}
					{adminOpen ? (
						<ul
							className="space-y-0.5 rounded-b-lg pb-1.5 pl-3 pt-1"
							style={{ background: palette.accentSoft }}
							{...(hasTabs ? { role: "tablist", "aria-labelledby": adminToggleId, "aria-orientation": "vertical" } : {})}
						>
							{ADMIN_NAV.map((item) => (
								<li key={item.id} role={hasTabs ? "presentation" : undefined}>
									<NavRow
										item={item}
										sub
										active={item.id === activeNav}
										interactive={isInteractive(item.id)}
										onNavigate={onNavigate}
										panelId={panelId}
										compact={compact}
									/>
								</li>
							))}
						</ul>
					) : null}
				</div>
			</div>
			<p className="p-3 text-center text-[10px] text-neutral-400">Powered by UpSquad</p>
		</aside>
	);
}

interface HeaderProps {
	community: MockCommunity;
	viewer: MockPerson;
	fullBleed: boolean;
	compact: boolean;
}

function Header({ community, viewer, fullBleed, compact }: HeaderProps) {
	const desktopClass = compact ? "hidden sm:flex" : "hidden md:flex";
	const mobileClass = compact ? "flex sm:hidden" : "flex md:hidden";
	return (
		<header
			className={`${compact ? "h-11" : "h-12"} shrink-0 border-b bg-white ${compact ? "sm:bg-transparent" : "md:bg-transparent"}`}
			style={{ borderColor: MOCK_TOKENS.separator }}
			data-mock="header"
		>
			<div className={`${desktopClass} mx-auto h-full w-full items-center gap-3 px-3 ${fullBleed ? "" : "max-w-5xl"}`} aria-hidden="true">
				<div
					className="flex h-7 min-w-0 flex-1 items-center gap-2 rounded-xl bg-white px-2.5 text-[11px] text-neutral-500"
					style={{ boxShadow: MOCK_TOKENS.surfaceShadow }}
				>
					<Search className="size-3.5 shrink-0" />
					<span className="truncate">Search members, posts, meetings, events…</span>
				</div>
				<div className="flex shrink-0 items-center justify-end gap-2">
					<span className="relative inline-flex size-7 items-center justify-center rounded-lg text-neutral-700">
						<Bell className="size-4" />
						<UnreadBadge count={3} floating />
					</span>
					<span className="flex items-center gap-1.5 rounded-lg px-1 py-0.5">
						<MockAvatar person={viewer} size={24} />
						<span className={`text-[11px] font-medium text-neutral-900 ${compact ? "hidden lg:inline" : ""}`}>{viewer.name}</span>
						<ChevronDown className="size-3.5 text-neutral-400" />
					</span>
				</div>
			</div>
			<div className={`${mobileClass} h-full items-center gap-2 px-3`} aria-hidden="true">
				<CommunityLogo size={26} rounded="rounded-lg" />
				<span className="truncate text-xs font-semibold text-neutral-900">{community.name}</span>
				<span className="flex-1" />
				<Search className="size-4 text-neutral-700" />
				<span className="relative inline-flex size-7 items-center justify-center">
					<Bell className="size-4 text-neutral-700" />
					<UnreadBadge count={3} floating />
				</span>
				<MockAvatar person={viewer} size={24} />
			</div>
		</header>
	);
}

export function WebAppShell({
	activeNav,
	children,
	community = MOCK_COMMUNITY,
	viewer = VIEWER,
	rightSidebar,
	fullBleed = false,
	layout = "desktop",
	onNavigate,
	navigable,
	panelId,
	navLabel,
	scroll = true,
	className = "",
}: WebAppShellProps) {
	const compact = layout === "compact";
	return (
		<div
			className={`flex h-full min-h-0 w-full overflow-hidden font-sans text-neutral-900 ${className}`}
			style={{ background: MOCK_TOKENS.background }}
			data-mock="web-shell"
		>
			{compact ? null : <CommunityRail community={community} />}
			<Sidebar
				community={community}
				activeNav={activeNav}
				onNavigate={onNavigate}
				navigable={navigable}
				panelId={panelId}
				navLabel={navLabel}
				compact={compact}
			/>
			<div className="flex min-w-0 flex-1 flex-col">
				<Header community={community} viewer={viewer} fullBleed={fullBleed} compact={compact} />
				<main
					className={`min-h-0 flex-1 overflow-x-hidden ${scroll ? "mock-scroll overflow-y-auto" : "overflow-y-hidden"}`}
					data-mock="content"
				>
					{fullBleed ? (
						<div className="flex h-full min-h-0 flex-col">{children}</div>
					) : (
						<div className={`mx-auto flex w-full max-w-5xl gap-4 ${compact ? "px-3 py-3" : "px-4 py-4"}`}>
							<div className="min-w-0 flex-1">{children}</div>
							{rightSidebar ? (
								<aside className="hidden w-56 shrink-0 lg:block" data-mock="right-sidebar">
									{rightSidebar}
								</aside>
							) : null}
						</div>
					)}
				</main>
			</div>
		</div>
	);
}
